/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { FilterItemValues } from '../../filters/filter/types/shared-filter-types.js';

/**
 * Hook that facilitates easy usage of the FilterBar for filtering entries in the DataTable.
 *
 * @param data - the initial, unfiltered data.
 * @param filter - custom filter function returning a boolean if the row should be filtered or not.
 *
 * @returns onChange callback and the filteredData which is then used as an input for the table
 * @public
 */
export function useFilteredData<T extends Record<string, any>>(
  data: Array<T>,
  filterData?: (
    appliedFilters: FilterItemValues,
    entry: T,
    index: number,
    originalEntries: Array<T>,
  ) => boolean,
): {
  onChange: (appliedFilters: FilterItemValues) => void;
  filteredData: T[];
} {
  const [filteredData, setFilteredData] = useState(data);
  const activeFilters = useRef<FilterItemValues>({});

  const onChange = useCallback(
    (appliedFilters: FilterItemValues) => {
      activeFilters.current = appliedFilters;
      setFilteredData(
        data.filter((entry, index) => {
          if (filterData) {
            return filterData(appliedFilters, entry, index, data);
          } else {
            return Object.keys(appliedFilters).every((filterName: string) => {
              return (
                entry[filterName] &&
                entry[filterName]
                  .toString()
                  .toLowerCase()
                  .includes(
                    (
                      (appliedFilters[filterName].value || '') as string
                    ).toLowerCase(),
                  )
              );
            });
          }
        }),
      );
    },
    [data, filterData],
  );

  // If the incoming data changes, the filter needs to be applied
  // again to the new dataset
  useEffect(() => {
    onChange(activeFilters.current);

    // Only needs to run when the data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return {
    onChange,
    filteredData,
  };
}
